import React, { forwardRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import useCurrentBreakpoint from 'hooks/ui/useCurrentBreakpoint';

const hasValue = value => value !== null && value !== undefined;

const FlexBox = forwardRef(
  ({ basis, direction, grow, shrink, wrap, gap, justifyContent, alignItems, row, column, children, xs, sm, md, lg, xl, ...rest }, ref) => {
    const currentBreakpoint = useCurrentBreakpoint();

    const sanitizedRest = useMemo(() => {
      // eslint-disable-next-line no-unused-vars
      const { display, flexBasis, flexDirection, flexGrow, flexShrink, flexWrap, ...others } = rest;
      return others;
    }, [rest]);

    const getInternalFlexProperty = useCallback(
      (property, value, isDefault) => {
        switch (currentBreakpoint) {
          case 'xs':
            return xs && typeof xs === 'object' && hasValue(xs[property]) ? xs[property] : typeof xs === 'string' && isDefault ? xs : value;
          case 'sm':
            return sm && typeof sm === 'object' && hasValue(sm[property]) ? sm[property] : typeof sm === 'string' && isDefault ? sm : value;
          case 'md':
            return md && typeof md === 'object' && hasValue(md[property]) ? md[property] : typeof md === 'string' && isDefault ? md : value;
          case 'lg':
            return lg && typeof lg === 'object' && hasValue(lg[property]) ? lg[property] : typeof lg === 'string' && isDefault ? lg : value;
          case 'xl':
            return xl && typeof xl === 'object' && hasValue(xl[property]) ? xl[property] : typeof xl === 'string' && isDefault ? xl : value;
          default:
            return value;
        }
      },
      [xs, sm, md, lg, xl, currentBreakpoint]
    );

    const internalBasis = useMemo(() => getInternalFlexProperty('basis', basis), [getInternalFlexProperty, basis]);
    const internalGrow = useMemo(() => getInternalFlexProperty('grow', grow), [getInternalFlexProperty, grow]);
    const internalShrink = useMemo(() => getInternalFlexProperty('shrink', shrink), [getInternalFlexProperty, shrink]);
    const internalWrap = useMemo(() => getInternalFlexProperty('wrap', wrap), [getInternalFlexProperty, wrap]);
    const internalDirection = useMemo(() => getInternalFlexProperty('direction', direction, true), [getInternalFlexProperty, direction]);
    const internalRow = useMemo(() => getInternalFlexProperty('direction', row), [getInternalFlexProperty, row]);
    const internalColumn = useMemo(() => getInternalFlexProperty('direction', column), [getInternalFlexProperty, column]);
    const internalGap = useMemo(() => getInternalFlexProperty('gap', gap), [getInternalFlexProperty, gap]);
    const internalJustifyContent = useMemo(
      () => getInternalFlexProperty('justifyContent', justifyContent),
      [getInternalFlexProperty, justifyContent]
    );
    const internalAlignItems = useMemo(() => getInternalFlexProperty('alignItems', alignItems), [getInternalFlexProperty, alignItems]);

    const normalizedDirection = useMemo(() => {
      if (internalDirection) return internalDirection;
      if (internalRow) return 'row';
      if (internalColumn) return 'column';
      return 'row';
    }, [internalDirection, internalRow, internalColumn]);

    const normalizedGrow = useMemo(() => {
      if (typeof internalGrow === 'boolean') return internalGrow ? 1 : 0;
      return internalGrow;
    }, [internalGrow]);

    const normalizedWrap = useMemo(() => {
      if (typeof internalWrap === 'boolean') return internalWrap ? 'wrap' : 'nowrap';
      return internalWrap;
    }, [internalWrap]);

    return (
      <Box
        ref={ref}
        display='flex'
        flexBasis={internalBasis}
        flexDirection={normalizedDirection}
        flexGrow={normalizedGrow}
        flexShrink={internalShrink}
        flexWrap={normalizedWrap}
        gap={internalGap}
        justifyContent={internalJustifyContent}
        alignItems={internalAlignItems}
        {...sanitizedRest}
      >
        {children}
      </Box>
    );
  }
);

FlexBox.displayName = 'FlexBox';

FlexBox.propTypes = {
  basis: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  direction: PropTypes.string,
  grow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shrink: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  wrap: PropTypes.string,
  gap: PropTypes.number,
  justifyContent: PropTypes.string,
  alignItems: PropTypes.string,
  row: PropTypes.bool,
  column: PropTypes.bool,
  xs: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  sm: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  md: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  lg: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  xl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  children: PropTypes.node
};

export default FlexBox;
